// LinkAgent — buyer landing page for invite links of the form
//   https://propwise.algonavis.com/link/agent/<agent_id>
//
// The agent shares this URL with a real buyer (WhatsApp, business
// card QR, etc.). When the buyer clicks it:
//
//   - signed in:    POST /auth/agents { agent_id } → status='active'
//                   redirect to /chat with a banner ("you're now linked
//                   to <agent name>")
//   - signed out:   redirect to /auth?next=/link/agent/<id> so the
//                   sign-up / sign-in flow can resume the link after.
//
// We never display the agent's id directly — only their name +
// agency (looked up via the GET /auth/agents response after the link
// is created). Keeps the page friendly even if the buyer pasted a
// truncated link.

import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthUser } from "@/lib/auth";
import { ApiError, apiPostJson } from "@/lib/api";
import { SHELL_TOKENS } from "@/components/agent-pro/ui";

type Status = "pending" | "linking" | "linked" | "needsAuth" | "error";

export function LinkAgent() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, pending: authPending } = useAuthUser();
  const [status, setStatus] = useState<Status>("pending");
  const [errMsg, setErrMsg] = useState<string>("");

  useEffect(() => {
    if (authPending) return;
    if (!id) {
      setStatus("error");
      setErrMsg(t("linkAgent.missingId", { defaultValue: "Invalid invite link." }));
      return;
    }
    if (!user) {
      setStatus("needsAuth");
      return;
    }
    setStatus("linking");
    apiPostJson("/auth/agents", { agent_id: id })
      .then(() => setStatus("linked"))
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 400) {
          // Self-link or unknown — surface the message verbatim.
          setErrMsg(e.message);
        } else if (e instanceof ApiError && e.status === 404) {
          setErrMsg(t("linkAgent.notFound", { defaultValue: "Agent not found." }));
        } else {
          setErrMsg(e instanceof Error ? e.message : String(e));
        }
        setStatus("error");
      });
  }, [authPending, user, id, t]);

  return (
    <div
      className="mx-auto max-w-md p-8"
      // LinkAgent renders top-level (no AppShellAgent wrapper), so the --ag-*
      // design tokens its children reference would be undefined → invisible
      // text. Spread SHELL_TOKENS here to define them for this subtree.
      style={{ ...SHELL_TOKENS, fontFamily: "var(--ag-font-ui)" }}
    >
      <h1
        className="text-2xl font-semibold mb-4"
        style={{ fontFamily: "var(--ag-font-serif)", color: "var(--ag-ink)" }}
      >
        {t("linkAgent.title", { defaultValue: "Connect to your agent" })}
      </h1>

      {status === "pending" || status === "linking" ? (
        <p style={{ color: "var(--ag-ink-3)" }}>
          {t("linkAgent.linking", { defaultValue: "Linking your account…" })}
        </p>
      ) : null}

      {status === "needsAuth" && (
        <div>
          <p style={{ color: "var(--ag-ink-2)", marginBottom: 12 }}>
            {t("linkAgent.signInPrompt", {
              defaultValue: "Sign in or create an account to connect with this agent.",
            })}
          </p>
          <Link
            to={`/auth?next=${encodeURIComponent(`/link/agent/${id ?? ""}`)}`}
            className="inline-block rounded-md px-4 py-2 text-[13px] font-medium"
            style={{ background: "var(--ag-accent)", color: "var(--ag-bg)" }}
          >
            {t("linkAgent.signInCta", { defaultValue: "Sign in / Sign up" })}
          </Link>
        </div>
      )}

      {status === "linked" && (
        <div>
          <p style={{ color: "var(--ag-ink-2)", marginBottom: 16 }}>
            {t("linkAgent.success", {
              defaultValue:
                "You're now connected. The agent will see your conversations with ProProp so they can help you better.",
            })}
          </p>
          <button
            onClick={() => navigate("/chat")}
            className="inline-block rounded-md px-4 py-2 text-[13px] font-medium"
            style={{ background: "var(--ag-accent)", color: "var(--ag-bg)" }}
          >
            {t("linkAgent.toChat", { defaultValue: "Start chatting" })}
          </button>
        </div>
      )}

      {status === "error" && (
        <div>
          <p style={{ color: "#bf3838", marginBottom: 12 }}>
            {t("linkAgent.failed", { defaultValue: "Couldn't link account." })} {errMsg && `— ${errMsg}`}
          </p>
          <Link
            to="/account"
            className="inline-block rounded-md px-4 py-2 text-[13px] font-medium border"
            style={{ borderColor: "var(--ag-rule)", color: "var(--ag-ink-2)" }}
          >
            {t("linkAgent.toAccount", { defaultValue: "Go to account" })}
          </Link>
        </div>
      )}
    </div>
  );
}
